import { formatDate } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from 'src/app/_services/base.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';
import { GlobalService } from 'src/app/_services/global.service';
import { ProductsService } from 'src/app/_services/products.service';
import { UserService } from 'src/app/_services/user.service';
import { environment } from 'src/environments/environment';

interface SetupStep {
  label: string;
  completed: boolean;
  expanded: boolean;
}

interface SummaryCard {
  title: string;
  value: string;
  trend: string;
  positiveTrend: boolean;
  hasTrend: boolean;
  inStore: string;
  online: string;
  highlighted?: boolean;
}

interface ProductRow {
  name: string;
  qty: string;
  image: string;
  price?: string;
  badge?: string;
  badgeType?: 'in' | 'low' | 'out';
}

interface SalesPoint {
  label: string;
  netSales: number;
  creditCard: number;
  cash: number;
}

interface FilterOption {
  label: string;
  value: string;
}

interface TopProduct {
  name: string;
  image: string;
  quantitySold: string;
}

enum StoreContentStatus {
  InProgress = 0,
  PendingByClient = 1,
  Completed = 2,
  Other = 3
}

@Component({
  selector: 'app-dashboard-v2',
  templateUrl: './dashboard-v2.component.html',
  styleUrls: ['./dashboard-v2.component.scss']
})
export class DashboardV2Component implements OnInit, OnDestroy {
  @ViewChild('summaryRangeOp') summaryRangeOverlay?: OverlayPanel;

  private readonly pageBannerClass = 'dashboard-v2-banner-active';
  private readonly completedBannerDismissedStoragePrefix = 'dashboard-v2-store-content-completed-banner-dismissed';
  private readonly storeContentStatusOverridePrefix = 'dashboard-v2-store-content-status-override';
  private readonly storeContentStatusDoing = 'doing';
  private readonly hiddenSetupGuideIdentifiers = new Set([
    '341f6f00-fa25-430d-a31a-5aad0a88a469',
    'younksnutritioncentral@yahoo.com',
    'zilonwebmaster@gmail.com'
  ]);
  private readonly templateStepLabel = 'Select Online Store Template';
  private readonly productsStepLabel = 'Add Products';
  private readonly shippingStepLabel = 'Set-up Shipping for Online Store';
  private readonly paymentsStepLabel = 'Set-up Payments';
  private readonly storeContentStepLabel = 'Add Store Content';
  readonly lowStockThreshold = 10;
  private readonly maxStoreContentFileSize = 25 * 1024 * 1024;
  private readonly allowedStoreContentExtensions = ['doc', 'docx', 'pdf'];
  private readonly defaultPendingBannerMessage =
    'Your store content file is still pending. Please upload it to start your online store setup.';
  private readonly defaultInProgressBannerMessage =
    'Your online store is being set up. Your store link will be available once setup is complete.';
  private readonly defaultCompletedBannerMessage =
    'Your store content request is completed successfully. Your online store is production ready.';
  private summaryWidgetsSubscription?: Subscription;
  private topSoldSubscription?: Subscription;
  private lowStockSubscription?: Subscription;
  private topProductSubscription?: Subscription;
  private salesSubscription?: Subscription;
  private setupProgressSubscription?: Subscription;
  private storeContentUploadSubscription?: Subscription;
  private storeContentConfigSubscription?: Subscription;
  private isUserInfoRefreshInProgress = false;
  private pendingSetupOpenTarget: string | null = null;
  readonly defaultProductImage = 'assets/images/default_product.png';
  readonly summarySkeletonCards = [1, 2, 3, 4];
  readonly tableSkeletonRows = [1, 2, 3, 4, 5];
  isSummaryLoading = true;
  isTablesLoading = true;
  isSalesLoading = true;
  isTopProductLoading = true;
  isSetupLoading = true;
  isSetupGuideHiddenForUser = false;
  isSetupOpen = false;
  isStoreContentEnabled = false;
  isStoreContentUiReady = false;
  storeContentPending = true;
  storeContentStatus: StoreContentStatus | null = StoreContentStatus.PendingByClient;
  storeContentFileUrl = '';
  storeContentShowNotificationBar = true;
  isStoreContentUploadLocked = false;
  isCompletedStatusBannerDismissed = false;
  isStoreContentUploading = false;
  storeContentUploadProgress = 0;
  storeContentUploadStatusText = '';
  storeContentUploadError = '';
  storeContentPendingMessage = this.defaultPendingBannerMessage;
  storeContentCompletedMessage = this.defaultCompletedBannerMessage;
  selectedSummaryRange = 'Today';
  selectedSalesRange = 'Last Week';
  selectedTopProductRange = 'Today';

  readonly storeContentTemplatePath = 'assets/templates/free-online-store-setup-content.docx';
  readonly filterOptions: FilterOption[] = [
    { label: 'Today', value: 'Today' },
    { label: 'Last 7 Days', value: 'Last Week' },
    { label: 'Current Month', value: 'Current Month' },
    { label: 'Current Year', value: 'Current Year' }
  ];
  readonly topProductFilterOptions: FilterOption[] = [
    { label: 'Today', value: 'Today' },
    { label: 'Current Month', value: 'Current Month' },
    { label: 'Current Year', value: 'Current Year' }
  ];
  readonly summaryRangeItems: MenuItem[] = [
    { label: 'Today', command: () => this.onSelectSummaryRange('Today') },
    { label: 'Current Month', command: () => this.onSelectSummaryRange('Current Month') },
    { label: 'Current Year', command: () => this.onSelectSummaryRange('Current Year') }
  ];

  setupSteps: SetupStep[] = [
    { label: this.templateStepLabel, completed: false, expanded: false },
    { label: this.productsStepLabel, completed: false, expanded: false },
    { label: this.shippingStepLabel, completed: false, expanded: false },
    { label: this.paymentsStepLabel, completed: false, expanded: false }
  ];

  summaryCards: SummaryCard[] = this.getEmptySummaryCards();

  bestSellingProducts: ProductRow[] = [];

  lowStockProducts: ProductRow[] = [];
  topProduct: TopProduct = {
    name: '',
    image: this.defaultProductImage,
    quantitySold: '0'
  };

  salesSeries: SalesPoint[] = [{ label: formatDate(new Date(), 'd MMM', 'en-US'), netSales: 0, creditCard: 0, cash: 0 }];
  averageDailySale = '$0.00';

  constructor(
    private readonly baseService: BaseService,
    private readonly appBaseDataService: AppBaseDataService,
    private readonly userService: UserService,
    private readonly productsService: ProductsService,
    private readonly businessSetupService: BusinessSetupService,
    private readonly businessSetupProgressService: BusinessSetupProgressService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  /** This card is entirely online-store-focused ("Your online store is in progress...") — same
   *  signal as business-setup-checklist.component.ts's domainStepNeeded/shippingStepNeeded. */
  get sellsOnline(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    return !!(setup?.standardOnlineStore ?? setup?.StandardOnlineStore);
  }

  /** Same source as the Hardware/Online-Store menu gating in app-base-data.service.ts. */
  get sellsInStore(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    return !!(setup?.hasPOS ?? setup?.HasPOS ?? setup?.hasPOSLite ?? setup?.HasPOSLite);
  }

  private get isYounksUser(): boolean {
    const user = GlobalService.userInfo;
    const identity = String(user?.username || user?.userName || user?.email || '').trim().toLowerCase();
    return identity === 'younksnutritioncentral@yahoo.com';
  }

  /** Summary-card In-Store / Online breakdown rows: Younks sees both; everyone else sees only the
   *  channels they sell on. If neither flag is set (legacy accounts) show both rather than nothing. */
  get showInStoreBreakdown(): boolean {
    return this.isYounksUser || this.sellsInStore || !this.sellsOnline;
  }

  get showOnlineBreakdown(): boolean {
    return this.isYounksUser || this.sellsOnline || !this.sellsInStore;
  }

  get setupCompletedCount(): number {
    return this.setupSteps.filter((step) => step.completed).length;
  }

  get setupTotalCount(): number {
    return this.setupSteps.length;
  }

  get setupProgressPercent(): number {
    return Math.round((this.setupCompletedCount / this.setupTotalCount) * 100);
  }

  get setupBannerText(): string {
    if (this.storeContentStatus === StoreContentStatus.InProgress) {
      return this.defaultInProgressBannerMessage;
    }

    if (this.storeContentStatus === StoreContentStatus.Completed) {
      return this.defaultCompletedBannerMessage;
    }

    if (this.storeContentStatus === StoreContentStatus.PendingByClient || this.storeContentPending) {
      return this.storeContentPendingMessage;
    }

    return this.storeContentPendingMessage;
  }

  get isCompletedStatus(): boolean {
    return this.storeContentStatus === StoreContentStatus.Completed;
  }

  get shouldShowStatusStrip(): boolean {
    if (!this.isStoreContentEnabled || !this.isStoreContentUiReady) {
      return false;
    }

    if (!this.storeContentShowNotificationBar) {
      return false;
    }

    return !(this.isCompletedStatus && this.isCompletedStatusBannerDismissed);
  }

  get canDismissStatusStrip(): boolean {
    return this.isCompletedStatus;
  }

  get shouldShowSummaryDrillDownIcon(): boolean {
    return ['Today', 'Current Month', 'Current Year'].includes(this.selectedSummaryRange);
  }

  get currentStoreContentStatusText(): string {
    if (this.storeContentStatus === StoreContentStatus.InProgress) {
      return 'In Progress';
    }
    if (this.storeContentStatus === StoreContentStatus.PendingByClient) {
      return 'Pending';
    }
    if (this.storeContentStatus === StoreContentStatus.Completed) {
      return 'Completed';
    }
    if (this.storeContentStatus === StoreContentStatus.Other) {
      return 'Other';
    }
    if (this.storeContentStatus === null) {
      return 'Not Started';
    }
    return this.storeContentPending ? 'Pending' : 'In Progress';
  }

  get canUploadStoreContent(): boolean {
    return !this.isStoreContentUploading && !this.isStoreContentUploadLocked && !this.hasExistingStoreContentFile && !this.isStoreContentInProgress;
  }

  get isStoreContentInProgress(): boolean {
    return this.storeContentStatus === StoreContentStatus.InProgress;
  }

  get hasExistingStoreContentFile(): boolean {
    return String(this.storeContentFileUrl || '').trim().length > 0;
  }

  get storeContentUploadDisabledReason(): string {
    if (this.isStoreContentInProgress) {
      return 'Your store content file has been uploaded successfully and is currently in progress.';
    }

    if (this.hasExistingStoreContentFile) {
      return 'Your store content file has already been uploaded.';
    }

    if (this.isStoreContentUploadLocked) {
      return 'Store content file has already been uploaded once. Re-upload is not allowed.';
    }

    return '';
  }

  get maxChartValue(): number {
    const allValues = this.salesSeries.flatMap((point) => [point.netSales, point.creditCard, point.cash]);
    return Math.max(...allValues, 1);
  }

  get chartAxisMax(): number {
    const maxValue = this.maxChartValue;
    if (maxValue <= 0) {
      return 1;
    }

    const roughStep = maxValue / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;
    const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    const step = niceNormalized * magnitude;

    return Math.max(step * 4, 1);
  }

  get chartYAxisTicks(): number[] {
    const step = this.chartAxisMax / 4;
    return [4, 3, 2, 1, 0].map((factor) => step * factor);
  }

  get hasChartData(): boolean {
    return this.salesSeries.some((point) => point.netSales > 0 || point.creditCard > 0 || point.cash > 0);
  }

  get chartGridTemplateColumns(): string {
    const dayCount = Math.max(this.salesSeries.length, 1);
    const minColumnWidth = window.innerWidth <= 575 ? 56 : 84;
    return `repeat(${dayCount}, minmax(${minColumnWidth}px, 1fr))`;
  }

  ngOnInit(): void {
    if (this.appBaseDataService.isAdmin) {
      this.router.navigateByUrl(this.appBaseDataService.getDashboardRoute());
      return;
    }

    this.initializeDashboard();
  }

  private async initializeDashboard(): Promise<void> {
    this.isSetupGuideHiddenForUser = this.shouldHideSetupGuideForCurrentUser();
    this.captureSetupOpenRequest();
    await this.ensureFreshUserInfo();
    this.isSetupGuideHiddenForUser = this.shouldHideSetupGuideForCurrentUser();

    this.isCompletedStatusBannerDismissed = this.readCompletedBannerDismissedState();
    this.isStoreContentEnabled = this.resolveStoreContentEnabledFlag();
    this.syncBannerBodyClass();
    if (this.isStoreContentEnabled) {
      this.loadStoreContentRuntimeConfig();
    } else {
      this.storeContentShowNotificationBar = false;
      this.storeContentPending = false;
      this.storeContentStatus = null;
      this.isStoreContentUiReady = true;
      this.isSetupLoading = false;
    }
    this.loadSetupProgress();
    this.loadSummaryWidgets();
    this.loadProductTables();
    this.loadTopProduct();
    this.loadDailySales();
  }

  ngOnDestroy(): void {
    this.removeBannerBodyClass();
    this.summaryWidgetsSubscription?.unsubscribe();
    this.topSoldSubscription?.unsubscribe();
    this.lowStockSubscription?.unsubscribe();
    this.topProductSubscription?.unsubscribe();
    this.salesSubscription?.unsubscribe();
    this.setupProgressSubscription?.unsubscribe();
    this.storeContentUploadSubscription?.unsubscribe();
    this.storeContentConfigSubscription?.unsubscribe();
  }

  toggleSetupCard(): void {
    this.isSetupOpen = !this.isSetupOpen;
  }

  dismissStatusStrip(): void {
    if (!this.canDismissStatusStrip) {
      return;
    }

    this.isCompletedStatusBannerDismissed = true;
    this.saveCompletedBannerDismissedState(true);
    this.syncBannerBodyClass();
  }

  getBarHeight(value: number): number {
    if (value <= 0) {
      return 0;
    }

    return Math.max((value / this.chartAxisMax) * 100, 4);
  }

  formatAxisTick(value: number): string {
    const currencyPrefix = GlobalService.getCurrencyCode() === 'PKR' ? 'PKR ' : '$';
    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);

    if (value === 0) {
      return `${currencyPrefix}0`;
    }

    if (absValue >= 1000) {
      return `${sign}${currencyPrefix}${this.formatCompactAxisValue(absValue)}`;
    }

    const fractionDigits = absValue < 10 ? 2 : 0;
    return `${sign}${currencyPrefix}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).format(absValue)}`;
  }

  private formatCompactAxisValue(absValue: number): string {
    if (absValue >= 1000000) {
      const compact = (absValue / 1000000).toFixed(absValue >= 10000000 ? 0 : 1).replace('.0', '');
      return `${compact}M`;
    }

    const compact = (absValue / 1000).toFixed(absValue >= 10000 ? 0 : 1).replace('.0', '');
    return `${compact}K`;
  }

  getBadgeClass(type: ProductRow['badgeType']): string {
    if (type === 'in') {
      return 'badge badge-in';
    }
    if (type === 'low') {
      return 'badge badge-low';
    }
    return 'badge badge-out';
  }

  onStoreContentUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input?.files || input.files.length === 0) {
      return;
    }

    if (!this.canUploadStoreContent) {
      this.storeContentUploadError = this.storeContentUploadDisabledReason || 'Upload is currently disabled.';
      this.storeContentUploadStatusText = '';
      this.storeContentUploadProgress = 0;
      input.value = '';
      return;
    }

    const file = input.files[0];
    const validationError = this.validateStoreContentFile(file);
    if (validationError) {
      this.storeContentUploadError = validationError;
      this.storeContentUploadStatusText = '';
      this.storeContentUploadProgress = 0;
      input.value = '';
      return;
    }

    this.storeContentUploadError = '';
    this.isStoreContentUploading = true;
    this.storeContentUploadProgress = 0;
    this.storeContentUploadStatusText = 'Preparing file for upload...';

    this.fileToDataUrl(file, (progress: number) => {
        this.storeContentUploadProgress = progress;
        this.storeContentUploadStatusText = `Preparing file for upload ${progress}%`;
      })
      .then((fileUrl) => {
        const payload = this.buildStoreContentUploadPayload(fileUrl, file.name);
        this.storeContentUploadSubscription?.unsubscribe();
        this.storeContentUploadSubscription = this.businessSetupService
          .uploadStoreContentFileWithProgress(payload)
          .subscribe({
            next: async (event: any) => {
              if (event.type === HttpEventType.Sent) {
                this.storeContentUploadStatusText = 'Uploading 0%';
                return;
              }

              if (event.type === HttpEventType.UploadProgress) {
                const total = Number(event.total || 0);
                const loaded = Number(event.loaded || 0);
                const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
                this.storeContentUploadProgress = progress;
                this.storeContentUploadStatusText = `Uploading ${progress}%`;
                return;
              }

              if (event.type === HttpEventType.Response) {
                const res = event.body;
                if (res?.success) {
                  this.storeContentUploadProgress = 100;
                  this.storeContentUploadStatusText = 'File uploaded successfully. Your setup request is now in progress.';
                  const responseData = this.getResponseData(res);
                  const resolvedStatus =
                    this.extractStoreContentStatus(responseData) ?? StoreContentStatus.InProgress;
                  const resolvedFileUrl =
                    this.extractStoreContentFileUrl(responseData)
                    || this.storeContentFileUrl
                    || this.buildLocalStoreContentUploadToken(file.name);
                  this.storeContentStatus =
                    resolvedStatus;
                  this.storeContentFileUrl = resolvedFileUrl;
                  const showNotificationFromResponse = this.extractStoreContentShowNotificationBar(responseData);
                  if (showNotificationFromResponse !== null) {
                    this.storeContentShowNotificationBar = showNotificationFromResponse;
                  }
                  this.isStoreContentUploadLocked = true;
                  this.storeContentPending = this.resolveStoreContentPending(this.storeContentStatus, true);
                  this.saveStoreContentStatusOverride(this.storeContentStatus);
                  this.persistStoreContentClientState();
                  if (this.isCompletedStatus) {
                    this.saveCompletedBannerDismissedState(false);
                    this.isCompletedStatusBannerDismissed = false;
                  }
                  this.syncBannerBodyClass();
                  this.updateStoreContentStepState(false);
                  await this.refreshStoreContentStateAfterUpload();
                } else {
                  this.storeContentUploadError = res?.message || 'File upload failed. Please try again.';
                  this.storeContentUploadStatusText = '';
                  this.storeContentUploadProgress = 0;
                }
                this.isStoreContentUploading = false;
                input.value = '';
              }
            },
            error: (error: any) => {
              this.isStoreContentUploading = false;
              this.storeContentUploadProgress = 0;
              this.storeContentUploadStatusText = '';
              this.storeContentUploadError =
                error?.error?.message || 'File upload failed. Please try again.';
              input.value = '';
            }
          });
      })
      .catch(() => {
        this.isStoreContentUploading = false;
        this.storeContentUploadProgress = 0;
        this.storeContentUploadStatusText = '';
        this.storeContentUploadError = 'Unable to process selected file.';
        input.value = '';
      });
  }

  downloadStoreContentTemplate(): void {
    const link = document.createElement('a');
    link.href = this.storeContentTemplatePath;
    link.download = 'Free-Online-Store-Setup-Content.docx';
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();
  }

  toggleSetupStep(step: SetupStep): void {
    if (!this.isSetupStepExpandable(step)) {
      return;
    }

    this.setupSteps = this.setupSteps.map((item) =>
      item.label === step.label ? { ...item, expanded: !item.expanded } : { ...item, expanded: false }
    );
  }

  isSetupStepExpandable(step: SetupStep): boolean {
    return this.isStoreContentStep(step) || this.hasStepAction(step);
  }

  isStoreContentStep(step: SetupStep): boolean {
    return step.label === this.storeContentStepLabel;
  }

  hasStepAction(step: SetupStep): boolean {
    return (
      step.label === this.templateStepLabel
      || step.label === this.productsStepLabel
      || step.label === this.shippingStepLabel
      || step.label === this.paymentsStepLabel
    );
  }

  shouldShowStepAction(step: SetupStep): boolean {
    return this.hasStepAction(step) && !step.completed && step.label !== this.templateStepLabel;
  }

  getStepDescription(step: SetupStep): string {
    if (step.label === this.templateStepLabel) {
      return 'Select a storefront template for your online business.';
    }

    if (step.label === this.productsStepLabel) {
      return 'Add or import your products with name, image, description, and price.';
    }

    if (step.label === this.shippingStepLabel) {
      return 'Configure delivery and pickup options available to your customers.';
    }

    if (step.label === this.paymentsStepLabel) {
      return 'Connect payment processing so customers can pay online securely.';
    }

    return '';
  }

  getStepActionLabel(step: SetupStep): string {
    if (step.label === this.templateStepLabel) {
      return 'Select Template';
    }

    if (step.label === this.productsStepLabel) {
      return 'Add Products';
    }

    if (step.label === this.shippingStepLabel) {
      return 'Shipping Configuration';
    }

    if (step.label === this.paymentsStepLabel) {
      return 'Set-up Payments';
    }

    return '';
  }

  onStepAction(step: SetupStep, event: Event): void {
    event.stopPropagation();

    if (step.label === this.templateStepLabel) {
      this.router.navigate(['/select-template']);
      return;
    }

    if (step.label === this.productsStepLabel) {
      this.router.navigate(['/app/products']);
      return;
    }

    if (step.label === this.shippingStepLabel) {
      this.router.navigate(['/app/business'], { queryParams: { page: 'shipping' } });
      return;
    }

    if (step.label === this.paymentsStepLabel) {
      this.router.navigate(['/app/business'], { queryParams: { page: 'payment-processing' } });
    }
  }

  onSelectSummaryRange(value: string): void {
    if (this.selectedSummaryRange === value) {
      return;
    }

    this.selectedSummaryRange = value;
    this.loadSummaryWidgets();
    this.loadProductTables();
  }

  onSummaryRangeOverlayShow(): void {
    setTimeout(() => {
      const overlay = this.summaryRangeOverlay;
      const el = overlay?.container;
      const target = overlay?.target as HTMLElement;
      if (!el || !target) {
        return;
      }

      const panelWidth = el.offsetWidth;
      const targetRect = target.getBoundingClientRect();
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
      const viewportRight = scrollX + window.innerWidth;

      // Align popup right edge with Today button right edge.
      let nextLeft = targetRect.right + scrollX - panelWidth;
      nextLeft = Math.max(0, Math.min(nextLeft, viewportRight - panelWidth - 8));
      el.style.left = `${nextLeft}px`;

      // Place caret near right side of popup, under the button's right area.
      const arrowLeft = Math.max(0, targetRect.right + scrollX - nextLeft - 28);
      el.style.setProperty('--overlayArrowLeft', `${arrowLeft}px`);
    });
  }

  onTopProductRangeChange(): void {
    this.loadTopProduct();
  }

  onSalesRangeChange(): void {
    this.loadDailySales();
  }

  goToDailySalesReport(): void {
    const [startDate, endDate] = this.getReportDateRange(this.selectedSummaryRange);

    this.router.navigate(['/app/daily-sales-report-v2'], {
      queryParams: {
        range: this.getReportRangeQueryValue(this.selectedSummaryRange),
        startDate: formatDate(startDate, 'yyyy-MM-dd', 'en-US'),
        endDate: formatDate(endDate, 'yyyy-MM-dd', 'en-US')
      }
    });
  }

  private loadSummaryWidgets(): void {
    this.summaryWidgetsSubscription?.unsubscribe();
    this.isSummaryLoading = true;
    const dashboardFilter = this.getDashboardFilter(this.selectedSummaryRange);
    const dateRangeParams = this.getDashboardDateRangeParams(this.selectedSummaryRange);
    const topWidgetsPath = environment.GetTopWidgetsV3 || environment.GetTopWidgets;
    const url = `${environment.InventoryBaseURLSTG}${topWidgetsPath}?dashboardFilter=${dashboardFilter}${dateRangeParams}`;
    const legacyUrl = `${environment.InventoryBaseURL}${environment.GetTopWidgets}?dashboardFilter=${dashboardFilter}${dateRangeParams}`;

    const applySummaryResponse = (res: any): void => {
      if (!res?.success || !res?.data) {
        this.summaryCards = this.getEmptySummaryCards();
        this.isSummaryLoading = false;
        return;
      }

      const data = res.data;
      const currentCards = this.getEmptySummaryCards();
      const previousTotalSales = Number(data.previousTotalSales || 0);
      const previousTotalOrders = Number(data.previousTotalOrders || 0);
      const previousTotalProfit = Number(data.previousTotalProfit || 0);
      const previousTotalRevenue = Number(data.previousTotalRevenue || 0);
      const saleDifference = Number(data.totalSales || 0) - previousTotalSales;
      const orderDifference = Number(data.totalOrders || 0) - previousTotalOrders;
      const profitDifference = Number(data.totalProfit || 0) - previousTotalProfit;
      const revenueDifference = Number(data.totalRevenue || 0) - previousTotalRevenue;
      const saleDifferencePercent = (saleDifference / (previousTotalSales === 0 ? 100 : previousTotalSales)) * 100;
      const orderDifferencePercent = (orderDifference / (previousTotalOrders === 0 ? 100 : previousTotalOrders)) * 100;
      const profitDifferencePercent = (profitDifference / (previousTotalProfit === 0 ? 100 : previousTotalProfit)) * 100;
      const revenueDifferencePercent =
        (revenueDifference / (previousTotalRevenue === 0 ? 100 : previousTotalRevenue)) * 100;

      this.summaryCards = [
        {
          ...currentCards[0],
          value: this.formatCurrency(data.totalSales),
          trend: this.formatPercent(saleDifferencePercent),
          positiveTrend: saleDifferencePercent >= 0,
          hasTrend: Number(data.totalSales || 0) !== 0 || previousTotalSales !== 0,
          inStore: this.formatCurrency(data.totalOfflineSales),
          online: this.formatCurrency(data.totalOnlineSales)
        },
        {
          ...currentCards[1],
          value: this.formatInteger(data.totalOrders),
          trend: this.formatPercent(orderDifferencePercent),
          positiveTrend: orderDifferencePercent >= 0,
          hasTrend: Number(data.totalOrders || 0) !== 0 || previousTotalOrders !== 0,
          inStore: this.formatInteger(data.totalOfflineOrders),
          online: this.formatInteger(data.totalOnlineOrders)
        },
        {
          ...currentCards[2],
          value: this.formatCurrency(data.totalProfit),
          trend: this.formatPercent(profitDifferencePercent),
          positiveTrend: profitDifferencePercent >= 0,
          hasTrend: Number(data.totalProfit || 0) !== 0 || previousTotalProfit !== 0,
          inStore: this.formatCurrency(data.totalOfflineProfit),
          online: this.formatCurrency(data.totalOnlineProfit)
        },
        {
          ...currentCards[3],
          value: this.formatCurrency(data.totalRevenue),
          trend: this.formatPercent(revenueDifferencePercent),
          positiveTrend: revenueDifferencePercent >= 0,
          hasTrend: Number(data.totalRevenue || 0) !== 0 || previousTotalRevenue !== 0,
          inStore: this.formatCurrency(data.totalOfflineRevenue),
          online: this.formatCurrency(data.totalOnlineRevenue)
        }
      ];
      this.isSummaryLoading = false;
    };

    this.summaryWidgetsSubscription = this.baseService.httpGet(url).subscribe({
      next: (res) => {
        applySummaryResponse(res);
      },
      error: (error: any) => {
        const shouldFallbackToLegacy =
          error?.status === 404 &&
          !!environment.GetTopWidgets &&
          topWidgetsPath === environment.GetTopWidgetsV3 &&
          environment.GetTopWidgetsV3 !== environment.GetTopWidgets;

        if (shouldFallbackToLegacy) {
          this.summaryWidgetsSubscription = this.baseService.httpGet(legacyUrl).subscribe({
            next: (legacyRes) => applySummaryResponse(legacyRes),
            error: () => {
              this.summaryCards = this.getEmptySummaryCards();
              this.isSummaryLoading = false;
            }
          });
          return;
        }

        this.summaryCards = this.getEmptySummaryCards();
        this.isSummaryLoading = false;
      }
    });
  }

  private getDashboardDateRangeParams(rangeLabel: string): string {
    const [, endDate] = this.getReportDateRange(rangeLabel);
    return `&date=${formatDate(endDate, 'yyyy-MM-dd', 'en-US')}`;
  }

  private getDashboardFilter(rangeLabel: string): string {
    if (rangeLabel === 'Current Month') {
      return '1';
    }
    if (rangeLabel === 'Current Year') {
      return '2';
    }
    return '0';
  }

  private loadProductTables(): void {
    const dashboardFilter = this.getDashboardFilter(this.selectedSummaryRange);
    const dateRangeParams = this.getDashboardDateRangeParams(this.selectedSummaryRange);
    const topSoldUrl = `${environment.InventoryBaseURL}${environment.GetTopSoldProducts}?dashboardFilter=${dashboardFilter}${dateRangeParams}`;
    const lowStockUrl = `${environment.InventoryBaseURL}${environment.GetLowStockProducts}?dashboardFilter=${dashboardFilter}${dateRangeParams}`;
    this.isTablesLoading = true;
    let pendingRequests = 2;
    const finishLoading = () => {
      pendingRequests -= 1;
      if (pendingRequests <= 0) {
        this.isTablesLoading = false;
      }
    }; 

    this.topSoldSubscription?.unsubscribe();
    this.lowStockSubscription?.unsubscribe();

    this.topSoldSubscription = this.baseService.httpGet(topSoldUrl).subscribe({
      next: (res) => {
        if (!res?.success || !Array.isArray(res?.data)) {
          this.bestSellingProducts = [];
          finishLoading();
          return;
        }

        this.bestSellingProducts = res.data
          .filter((item: any) => this.hasDashboardProductName(item))
          .map((item: any) => ({
          name: this.getDashboardProductName(item),
          qty: this.formatInteger(item?.soldQuantity),
          image: this.resolveDashboardProductImage(item)
        }));
        finishLoading();
      },
      error: () => {
        this.bestSellingProducts = [];
        finishLoading();
      }
    });

    this.lowStockSubscription = this.baseService.httpGet(lowStockUrl).subscribe({
      next: (res) => {
        if (!res?.success || !Array.isArray(res?.data)) {
          this.lowStockProducts = [];
          finishLoading();
          return;
        }

        this.lowStockProducts = res.data
          .filter((item: any) => !this.isDeletedProduct(item) && this.hasDashboardProductName(item))
          .map((item: any) => ({
            ...this.mapLowStockRow(item)
          }));
        finishLoading();
      },
      error: () => {
        this.lowStockProducts = [];
        finishLoading();
      }
    });
  }

  private loadTopProduct(): void {
    const dashboardFilter = this.getDashboardFilter(this.selectedTopProductRange);
    const dateRangeParams = this.getDashboardDateRangeParams(this.selectedTopProductRange);
    const url = `${environment.InventoryBaseURL}${environment.GetTopWidgets}?dashboardFilter=${dashboardFilter}${dateRangeParams}`;
    this.topProductSubscription?.unsubscribe();
    this.isTopProductLoading = true;

    this.topProductSubscription = this.baseService.httpGet(url).subscribe({
      next: (res) => {
        if (!res?.success || !res?.data) {
          this.topProduct = {
            name: '',
            image: this.defaultProductImage,
            quantitySold: '0'
          };
          this.isTopProductLoading = false;
          return;
        }

        const data = res.data;
        this.topProduct = {
          name: String(data?.productOfMonth || '').trim(),
          image: this.resolveDashboardProductImage({
            image: data?.productOfMonthPicture,
            productOfMonthThumbnail: data?.productOfMonthThumbnail
          }),
          quantitySold: this.formatInteger(data?.quantitySold)
        };
        this.isTopProductLoading = false;
      },
      error: () => {
        this.topProduct = {
          name: '',
          image: this.defaultProductImage,
          quantitySold: '0'
        };
        this.isTopProductLoading = false;
      }
    });
  }

  private loadDailySales(): void {
    const [startDate, endDate] = this.getSalesDateRange(this.selectedSalesRange);
    const payload = {
      ReportName: 'SaleReport',
      Parameter: {
        StartDate: formatDate(startDate, 'MM/dd/yyyy', 'en-US'),
        EndDate: formatDate(endDate, 'MM/dd/yyyy', 'en-US')
      }
    };

    const url = `${environment.InventoryBaseURL}${environment.Report}`;
    this.salesSubscription?.unsubscribe();
    this.isSalesLoading = true;
    this.salesSubscription = this.baseService.httpPost(url, JSON.stringify(payload)).subscribe({
      next: (res) => {
        const emptySeries = this.createEmptySalesSeries(this.selectedSalesRange);
        if (!res?.success || !Array.isArray(res?.data)) {
          this.salesSeries = emptySeries;
          this.averageDailySale = this.formatCurrency(0);
          this.isSalesLoading = false;
          return;
        }

        const sortedData = [...res.data].sort(
          (a: any, b: any) => this.getSalesItemTime(a) - this.getSalesItemTime(b)
        );

        const groupByMonth = this.selectedSalesRange === 'Current Year';
        this.salesSeries = this.mergeSalesSeriesWithAxisTemplate(
          this.buildSalesSeries(sortedData, groupByMonth),
          this.selectedSalesRange
        );

        if (this.salesSeries.length === 0) {
          this.salesSeries = emptySeries;
        }

        const totalNetSales = this.salesSeries.reduce((sum, point) => sum + Number(point?.netSales || 0), 0);
        const uniqueDayCount = this.getUniqueSalesDayCount(sortedData);
        const averageNetSale = totalNetSales / (uniqueDayCount > 0 ? uniqueDayCount : 1);
        this.averageDailySale = this.formatCurrency(averageNetSale);
        this.isSalesLoading = false;
      },
      error: () => {
        this.salesSeries = this.createEmptySalesSeries(this.selectedSalesRange);
        this.averageDailySale = this.formatCurrency(0);
        this.isSalesLoading = false;
      }
    });
  }

  private buildSalesSeries(items: any[], groupByMonth: boolean): SalesPoint[] {
    const bucketMap = new Map<
      string,
      { sortTime: number; label: string; netSales: number; creditCard: number; cash: number }
    >();

    for (const item of items) {
      const rawDate = this.getSalesItemDate(item);
      if (!rawDate) {
        continue;
      }

      const dayDate = new Date(rawDate.getFullYear(), rawDate.getMonth(), rawDate.getDate());
      const bucketDate = groupByMonth
        ? new Date(dayDate.getFullYear(), dayDate.getMonth(), 1)
        : dayDate;

      const bucketKey = groupByMonth
        ? `${bucketDate.getFullYear()}-${bucketDate.getMonth() + 1}`
        : `${bucketDate.getFullYear()}-${bucketDate.getMonth() + 1}-${bucketDate.getDate()}`;

      const label = groupByMonth
        ? formatDate(bucketDate, 'MMM', 'en-US')
        : formatDate(bucketDate, 'd MMM', 'en-US');

      const directNetSales = this.readOptionalSalesAmount(item, ['netSales', 'NetSales', 'netSale', 'NetSale']);
      const subTotal = this.readSalesAmount(item, ['subTotal', 'SubTotal', 'grandTotal', 'GrandTotal'], 0);
      const discount = this.readSalesAmount(item, ['discount', 'Discount'], 0);
      const netSales = Math.max(directNetSales ?? (subTotal - discount), 0);
      const creditCard = Math.max(
        this.readSalesAmount(item, ['creditCardAmount', 'CreditCardAmount', 'cardAmount', 'CardAmount'], 0),
        0
      );
      const cash = Math.max(this.readSalesAmount(item, ['cashAmount', 'CashAmount', 'cash', 'Cash'], 0), 0);

      const existingBucket = bucketMap.get(bucketKey);
      if (existingBucket) {
        existingBucket.netSales += netSales;
        existingBucket.creditCard += creditCard;
        existingBucket.cash += cash;
        continue;
      }

      bucketMap.set(bucketKey, {
        sortTime: bucketDate.getTime(),
        label,
        netSales,
        creditCard,
        cash
      });
    }

    return Array.from(bucketMap.values())
      .sort((a, b) => a.sortTime - b.sortTime)
      .map((point) => ({
        label: point.label,
        netSales: point.netSales,
        creditCard: point.creditCard,
        cash: point.cash
      }));
  }

  private getUniqueSalesDayCount(items: any[]): number {
    const uniqueDays = new Set<string>();
    for (const item of items) {
      const rawDate = this.getSalesItemDate(item);
      if (!rawDate) {
        continue;
      }
      uniqueDays.add(formatDate(rawDate, 'yyyy-MM-dd', 'en-US'));
    }
    return uniqueDays.size;
  }

  private createEmptySalesSeries(rangeLabel: string): SalesPoint[] {
    const [startDate, endDate] = this.getSalesDateRange(rangeLabel);
    const points: SalesPoint[] = [];

    if (rangeLabel === 'Current Year') {
      for (let month = 0; month <= endDate.getMonth(); month++) {
        const currentMonth = new Date(endDate.getFullYear(), month, 1);
        points.push({
          label: formatDate(currentMonth, 'MMM', 'en-US'),
          netSales: 0,
          creditCard: 0,
          cash: 0
        });
      }
      return points;
    }

    for (let cursor = new Date(startDate); cursor <= endDate; cursor.setDate(cursor.getDate() + 1)) {
      points.push({
        label: formatDate(cursor, 'd MMM', 'en-US'),
        netSales: 0,
        creditCard: 0,
        cash: 0
      });
    }

    return points;
  }

  private mergeSalesSeriesWithAxisTemplate(points: SalesPoint[], rangeLabel: string): SalesPoint[] {
    const template = this.createEmptySalesSeries(rangeLabel);
    if (template.length === 0) {
      return points;
    }

    const pointMap = new Map(points.map((point) => [point.label, point]));
    return template.map((templatePoint) => pointMap.get(templatePoint.label) ?? templatePoint);
  }

  private getSalesItemDate(item: any): Date | null {
    const rawValue =
      item?.date
      ?? item?.Date
      ?? item?.salesDate
      ?? item?.SalesDate
      ?? item?.saleDate
      ?? item?.SaleDate
      ?? item?.createdDate
      ?? item?.CreatedDate;

    if (!rawValue) {
      return null;
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }

  private getSalesItemTime(item: any): number {
    return this.getSalesItemDate(item)?.getTime() ?? 0;
  }

  private readSalesAmount(item: any, keys: string[], fallback: number): number {
    const value = this.readOptionalSalesAmount(item, keys);
    return value ?? fallback;
  }

  private readOptionalSalesAmount(item: any, keys: string[]): number | null {
    for (const key of keys) {
      const rawValue = item?.[key];
      const numericValue = Number(rawValue);
      if (rawValue !== null && rawValue !== undefined && rawValue !== '' && Number.isFinite(numericValue)) {
        return numericValue;
      }
    }

    return null;
  }

  private getSalesDateRange(rangeLabel: string): [Date, Date] {
    const today = new Date();
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startDate = new Date(endDate);

    if (rangeLabel === 'Today') {
      return [startDate, endDate];
    }

    if (rangeLabel === 'Current Month') {
      return [new Date(today.getFullYear(), today.getMonth(), 1), endDate];
    }

    if (rangeLabel === 'Current Year') {
      return [new Date(today.getFullYear(), 0, 1), endDate];
    }

    startDate.setDate(startDate.getDate() - 6);
    return [startDate, endDate];
  }

  private getReportDateRange(rangeLabel: string): [Date, Date] {
    const today = new Date();

    if (rangeLabel === 'Current Month') {
      return [
        new Date(today.getFullYear(), today.getMonth(), 1),
        new Date(today.getFullYear(), today.getMonth() + 1, 0)
      ];
    }

    if (rangeLabel === 'Current Year') {
      return [
        new Date(today.getFullYear(), 0, 1),
        new Date(today.getFullYear(), 11, 31)
      ];
    }

    const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return [currentDay, currentDay];
  }

  private getReportRangeQueryValue(rangeLabel: string): string {
    if (rangeLabel === 'Current Month') {
      return 'month';
    }

    if (rangeLabel === 'Current Year') {
      return 'year';
    }

    return 'today';
  }

  private loadSetupProgress(): void {
    const inventoryRequest = this.productsService
      .getAllProductList4InventoryV3({ PageNo: 0, PageSize: 1 })
      .pipe(
        map((res: any) => !!(res?.success && Array.isArray(res?.data) && res.data.length > 0)),
        catchError(() => of(false))
      );

    // getWebStoreConfiguration() always returns a template/webStoreHost for every account (an
    // auto-provisioned default, unrelated to any merchant action), so it can't be used as a
    // completion signal — only the explicit Save on the checklist's domain/template step counts.
    const hasTemplate = this.businessSetupProgressService.hasSavedDomainTemplate;

    // isEnabled is unconditionally true on every zone the UI ever writes, and also true on
    // whatever the backend auto-provisions before the merchant ever opens shipping settings — see
    // BusinessSetupProgressService's matching shippingRequest for the full explanation. isSaved,
    // OR'd with the hasSavedShippingConfiguration fallback, is the real signal.
    const shippingRequest = this.businessSetupService.getShippingConfiguration().pipe(
      map((res: any) => {
        const zones = res?.data?.zones;
        const zoneSaved = !!(res?.success && Array.isArray(zones) && zones.length > 0 && zones[0]?.isSaved === true);
        return zoneSaved || this.businessSetupProgressService.hasSavedShippingConfiguration;
      }),
      catchError(() => of(this.businessSetupProgressService.hasSavedShippingConfiguration))
    );

    const paymentRequest = this.businessSetupService.getPaymentProcessing().pipe(
      map((res: any) => !!(res?.success && res?.data?.isSaved === true)),
      catchError(() => of(false))
    );

    this.setupProgressSubscription?.unsubscribe();
    this.setupProgressSubscription = forkJoin({
      hasProducts: inventoryRequest,
      hasShipping: shippingRequest,
      hasPayment: paymentRequest
    }).subscribe((state) => {
      const setupSteps: SetupStep[] = [
        { label: this.templateStepLabel, completed: hasTemplate, expanded: false },
        { label: this.productsStepLabel, completed: state.hasProducts, expanded: false },
        { label: this.shippingStepLabel, completed: state.hasShipping, expanded: false },
        { label: this.paymentsStepLabel, completed: state.hasPayment, expanded: false }
      ];

      if (this.isStoreContentEnabled) {
        const isStoreContentCompleted = this.isStoreContentChecklistCompleted();
        setupSteps.push({
          label: this.storeContentStepLabel,
          completed: isStoreContentCompleted,
          expanded: !isStoreContentCompleted
        });
      }

      this.setupSteps = setupSteps;
      this.applySetupOpenRequest();
    });
  }

  private loadStoreContentRuntimeConfig(): void {
    if (!this.isStoreContentEnabled) {
      this.storeContentShowNotificationBar = false;
      this.storeContentPending = false;
      this.storeContentStatus = null;
      this.isStoreContentUiReady = true;
      this.isSetupLoading = false;
      return;
    }

    this.storeContentConfigSubscription?.unsubscribe();
    const useStoreContentContentApis = false;
    this.storeContentConfigSubscription = forkJoin({
      submission: useStoreContentContentApis
        ? this.businessSetupService.getStoreContentSubmission().pipe(catchError(() => of(null)))
        : of(null),
      banner: useStoreContentContentApis
        ? this.businessSetupService.getStoreContentBannerSettings().pipe(catchError(() => of(null)))
        : of(null),
      configuration: this.userService.getWebStoreConfiguration().pipe(catchError(() => of(null)))
    }).subscribe(({ submission, banner, configuration }: any) => {
      const submissionData = this.getResponseData(submission);
      const configurationData = this.getResponseData(configuration);
      const profileData = this.getBusinessSetupStoreContentData();
      const hasSubmission = !!(submission?.success && Array.isArray(submission?.data) && submission.data.length > 0);
      const localStatusOverride = this.readStoreContentStatusOverride();
      const hasLocalInProgressOverride = localStatusOverride === StoreContentStatus.InProgress;

      this.storeContentStatus =
        this.extractStoreContentStatus(profileData)
        ?? this.extractStoreContentStatus(configurationData)
        ?? this.extractStoreContentStatus(submissionData);
      this.storeContentFileUrl =
        this.extractStoreContentFileUrl(profileData)
        || this.extractStoreContentFileUrl(configurationData)
        || this.extractStoreContentFileUrl(submissionData);
      const showNotificationBar =
        this.extractStoreContentShowNotificationBar(profileData)
        ?? this.extractStoreContentShowNotificationBar(configurationData)
        ?? this.extractStoreContentShowNotificationBar(submissionData);
      if (showNotificationBar !== null) {
        this.storeContentShowNotificationBar = showNotificationBar;
      }

      if (
        hasLocalInProgressOverride
        && (this.storeContentStatus === null || this.storeContentStatus === StoreContentStatus.PendingByClient)
      ) {
        this.storeContentStatus = StoreContentStatus.InProgress;
        this.storeContentShowNotificationBar = true;
      }

      // Store content should be pending until a file URL exists.
      if (
        this.storeContentStatus === StoreContentStatus.InProgress
        && !this.hasExistingStoreContentFile
      ) {
        this.storeContentStatus = StoreContentStatus.PendingByClient;
        this.clearStoreContentStatusOverride();
      }

      if (
        localStatusOverride !== null
        && (
          this.storeContentStatus === StoreContentStatus.Completed
          || this.storeContentStatus === StoreContentStatus.Other
          || this.hasExistingStoreContentFile
        )
      ) {
        this.clearStoreContentStatusOverride();
      }

      this.isStoreContentUploadLocked = this.hasExistingStoreContentFile;
      this.storeContentPending = this.resolveStoreContentPending(this.storeContentStatus, hasSubmission);
      if (
        this.isStoreContentUntouched(
          this.storeContentStatus,
          hasSubmission,
          this.hasExistingStoreContentFile,
          this.storeContentShowNotificationBar
        )
      ) {
        this.storeContentStatus = null;
        this.storeContentPending = false;
        this.storeContentShowNotificationBar = false;
      }
      if (this.isCompletedStatus && !this.readCompletedBannerDismissedState()) {
        this.isCompletedStatusBannerDismissed = false;
      }

      this.applyStoreContentBannerMessages(banner);
      this.updateStoreContentStepState(this.storeContentPending);
      this.isStoreContentUiReady = true;
      this.isSetupLoading = false;
      this.syncBannerBodyClass();
    });
  }

  private applyStoreContentBannerMessages(res: any): void {
    this.storeContentPendingMessage = this.defaultPendingBannerMessage;
    this.storeContentCompletedMessage = this.defaultCompletedBannerMessage;

    if (!res?.success || !Array.isArray(res?.data) || res.data.length === 0) {
      return;
    }

    let pendingMessage = '';
    let completedMessage = '';

    for (const item of res.data) {
      const key = String(item?.contentType || '').trim().toLowerCase();
      const value = String(item?.content1 || '').trim();
      if (!value) {
        continue;
      }

      if (key === 'pendingmessage' || key === 'pending') {
        pendingMessage = value;
      }

      if (key === 'completedmessage' || key === 'successmessage' || key === 'completed') {
        completedMessage = value;
      }
    }

    if (!pendingMessage || !completedMessage) {
      const raw = String(res.data[0]?.content1 || '').trim();
      if (raw.startsWith('{') && raw.endsWith('}')) {
        try {
          const parsed = JSON.parse(raw);
          pendingMessage = pendingMessage || String(parsed?.pendingMessage || '').trim();
          completedMessage = completedMessage || String(parsed?.completedMessage || '').trim();
        } catch {
          // Ignore malformed JSON and fall back to defaults.
        }
      }
    }

    if (pendingMessage) {
      this.storeContentPendingMessage = pendingMessage;
    }
    if (completedMessage) {
      this.storeContentCompletedMessage = completedMessage;
    }
  }

  private updateStoreContentStepState(expanded: boolean): void {
    if (!this.isStoreContentEnabled) {
      return;
    }

    const isStoreContentCompleted = this.isStoreContentChecklistCompleted();
    this.setupSteps = this.setupSteps.map((step) =>
      step.label === this.storeContentStepLabel
        ? { ...step, completed: isStoreContentCompleted, expanded }
        : step
    );
    this.applySetupOpenRequest();
  }

  private captureSetupOpenRequest(): void {
    const requestedTarget = String(this.route.snapshot.queryParamMap.get('openSetup') || '').trim().toLowerCase();
    this.pendingSetupOpenTarget = requestedTarget || null;
  }

  private applySetupOpenRequest(): void {
    if (!this.pendingSetupOpenTarget) {
      return;
    }

    this.isSetupOpen = true;

    if (this.pendingSetupOpenTarget === 'store-content') {
      const hasStoreContentStep = this.setupSteps.some((step) => step.label === this.storeContentStepLabel);
      if (!hasStoreContentStep) {
        return;
      }

      this.setupSteps = this.setupSteps.map((step) => ({
        ...step,
        expanded: step.label === this.storeContentStepLabel
      }));
    }

    this.pendingSetupOpenTarget = null;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        openSetup: null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private isStoreContentChecklistCompleted(): boolean {
    if (!this.isStoreContentEnabled) {
      return false;
    }

    if (
      this.storeContentStatus === StoreContentStatus.InProgress
      || this.storeContentStatus === StoreContentStatus.Completed
      || this.storeContentStatus === StoreContentStatus.Other
    ) {
      return true;
    }

    if (this.hasExistingStoreContentFile || this.isStoreContentUploadLocked) {
      return true;
    }

    return false;
  }

  private toBooleanFlag(value: any): boolean {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1';
    }

    return value === true || value === 1;
  }

  private resolveStoreContentEnabledFlag(): boolean {
    const setup = this.getBusinessSetupStoreContentData();
    return this.toBooleanFlag(setup?.availSaveOffer ?? setup?.AvailSaveOffer) || this.readStoreContentStatusOverride() !== null;
  }

  private resolveStoreContentPending(status: StoreContentStatus | null, hasSubmission: boolean): boolean {
    if (status === StoreContentStatus.PendingByClient) {
      return true;
    }

    if (
      status === StoreContentStatus.InProgress ||
      status === StoreContentStatus.Completed ||
      status === StoreContentStatus.Other
    ) {
      return false;
    }

    return !hasSubmission;
  }

  private isStoreContentUntouched(
    status: StoreContentStatus | null,
    hasSubmission: boolean,
    hasFile: boolean,
    showNotificationBar: boolean
  ): boolean {
    if (hasSubmission || hasFile || showNotificationBar) {
      return false;
    }

    return (
      status === null
      || status === StoreContentStatus.InProgress
      || status === StoreContentStatus.PendingByClient
      || status === StoreContentStatus.Completed
    );
  }

  private getResponseData(res: any): any {
    if (!res?.success) {
      return null;
    }

    const data = res.data;
    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : null;
    }

    return data ?? null;
  }

  private getBusinessSetupStoreContentData(): any {
    const globalSetup = GlobalService.userInfo?.businessSetup?.[0];
    if (globalSetup) {
      return globalSetup;
    }

    const rawBusinessInfo = sessionStorage.getItem('business-Info');
    if (!rawBusinessInfo) {
      return null;
    }

    try {
      return JSON.parse(rawBusinessInfo);
    } catch {
      return null;
    }
  }

  private safeParseJson(raw: string | null): any {
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private hasLoadedUserInfo(): boolean {
    return !!(GlobalService.userInfo && Array.isArray(GlobalService.userInfo.businessSetup) && GlobalService.userInfo.businessSetup.length > 0);
  }

  private hydrateUserInfoFromSession(): boolean {
    if (this.hasLoadedUserInfo()) {
      return true;
    }

    const businessInfo = this.safeParseJson(sessionStorage.getItem('business-Info'));
    if (!businessInfo) {
      return false;
    }

    const localUser = this.safeParseJson(localStorage.getItem('user'));
    const loginUser = this.safeParseJson(sessionStorage.getItem('login-user'));
    const existing = GlobalService.userInfo || {};
    const userName =
      existing?.username
      || existing?.userName
      || localUser?.userName
      || loginUser?.Username
      || loginUser?.username
      || '';
    const userId =
      existing?.userID
      || existing?.userId
      || businessInfo?.userID
      || businessInfo?.userId
      || '';

    GlobalService.userInfo = {
      ...existing,
      userID: userId,
      userId: userId,
      username: userName,
      userName: userName,
      businessSetup: [businessInfo]
    };

    return this.hasLoadedUserInfo();
  }

  private ensureFreshUserInfo(): Promise<void> {
    return new Promise((resolve) => {
      this.hydrateUserInfoFromSession();
      this.userService.setCurrentUserFromLocalStorage();

      const localUser = this.safeParseJson(localStorage.getItem('user'));
      const loginUser = this.safeParseJson(sessionStorage.getItem('login-user'));
      const userName = String(
        GlobalService.userInfo?.username
        || GlobalService.userInfo?.userName
        || this.userService.currentUserObj?.userName
        || localUser?.userName
        || loginUser?.Username
        || loginUser?.username
        || ''
      ).trim();

      if (!userName || this.isUserInfoRefreshInProgress) {
        resolve();
        return;
      }

      this.isUserInfoRefreshInProgress = true;
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        this.isUserInfoRefreshInProgress = false;
        resolve();
      };
      const guard = setTimeout(() => finish(), 5000);

      this.userService.getUserByUserName(userName).subscribe({
        next: (res: any) => {
          if (res?.success && res?.data) {
            GlobalService.userInfo = res.data;
            const setup = res.data?.businessSetup?.[0];
            if (setup) {
              sessionStorage.setItem('business-Info', JSON.stringify(setup));
            }

            const rights = Array.isArray(res.data?.userRights)
              ? res.data.userRights
              : Array.isArray(res.data?.UserRights)
                ? res.data.UserRights
                : [];

            this.userService.loadPermissions(rights);
            this.appBaseDataService.initMenuList(false);
          }
        },
        error: () => {
          clearTimeout(guard);
          finish();
        },
        complete: () => {
          clearTimeout(guard);
          finish();
        }
      });
    });
  }

  private getCompletedBannerDismissedStorageKey(): string {
    const setup = this.getBusinessSetupStoreContentData();
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? 'default-user');
    return `${this.completedBannerDismissedStoragePrefix}-${userId}-${businessId}`;
  }

  private getStoreContentStatusOverrideStorageKey(): string {
    const setup = this.getBusinessSetupStoreContentData();
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? 'default-user');
    return `${this.storeContentStatusOverridePrefix}-${userId}-${businessId}`;
  }

  private saveStoreContentStatusOverride(status: StoreContentStatus | null): void {
    if (status === null) {
      this.clearStoreContentStatusOverride();
      return;
    }

    let rawStatus = '';
    if (status === StoreContentStatus.InProgress) {
      rawStatus = 'inprogress';
    } else if (status === StoreContentStatus.PendingByClient) {
      rawStatus = 'pendingbyclient';
    } else if (status === StoreContentStatus.Completed) {
      rawStatus = 'completed';
    } else if (status === StoreContentStatus.Other) {
      rawStatus = 'other';
    }

    if (!rawStatus) {
      return;
    }

    try {
      localStorage.setItem(this.getStoreContentStatusOverrideStorageKey(), rawStatus);
    } catch {
      // Ignore localStorage errors.
    }
  }

  private readStoreContentStatusOverride(): StoreContentStatus | null {
    let rawStatus = '';
    try {
      rawStatus = String(localStorage.getItem(this.getStoreContentStatusOverrideStorageKey()) || '').trim().toLowerCase();
    } catch {
      return null;
    }

    if (!rawStatus) {
      return null;
    }

    if (
      rawStatus === this.storeContentStatusDoing
      || rawStatus === 'inprogress'
      || rawStatus === 'in progress'
      || rawStatus === '0'
    ) {
      return StoreContentStatus.InProgress;
    }

    if (rawStatus === 'pendingbyclient' || rawStatus === 'pending by client' || rawStatus === '1') {
      return StoreContentStatus.PendingByClient;
    }

    if (rawStatus === 'completed' || rawStatus === '2') {
      return StoreContentStatus.Completed;
    }

    if (rawStatus === 'other' || rawStatus === '3') {
      return StoreContentStatus.Other;
    }

    return null;
  }

  private clearStoreContentStatusOverride(): void {
    try {
      localStorage.removeItem(this.getStoreContentStatusOverrideStorageKey());
    } catch {
      // Ignore localStorage errors.
    }
  }

  private buildLocalStoreContentUploadToken(fileName: string): string {
    const normalizedFileName = String(fileName || 'store-content-upload').trim() || 'store-content-upload';
    return `store-content-upload://${encodeURIComponent(normalizedFileName)}`;
  }

  private async refreshStoreContentStateAfterUpload(): Promise<void> {
    await this.ensureFreshUserInfo();
    this.isStoreContentEnabled = this.resolveStoreContentEnabledFlag();
    this.businessSetupService.notifyStoreContentStateRefresh();
    this.loadStoreContentRuntimeConfig();
    this.loadSetupProgress();
  }

  private persistStoreContentClientState(): void {
    this.businessSetupService.persistStoreContentClientState({
      availSaveOffer: true,
      storeContentFileUrl: this.storeContentFileUrl,
      storeContentShowNotificationBar: this.storeContentShowNotificationBar
    });
  }

  private shouldHideSetupGuideForCurrentUser(): boolean {
    const businessInfo = this.safeParseJson(sessionStorage.getItem('business-Info'));
    const localUser = this.safeParseJson(localStorage.getItem('user'));
    const loginUser = this.safeParseJson(sessionStorage.getItem('login-user'));
    const identifiers = [
      GlobalService.userInfo?.userID,
      GlobalService.userInfo?.userId,
      GlobalService.userInfo?.username,
      GlobalService.userInfo?.userName,
      GlobalService.userInfo?.email,
      this.userService.currentUserObj?.userID,
      this.userService.currentUserObj?.userName,
      localUser?.userID,
      localUser?.userId,
      localUser?.id,
      localUser?.userName,
      localUser?.username,
      localUser?.email,
      loginUser?.UserID,
      loginUser?.userID,
      loginUser?.Username,
      loginUser?.username,
      loginUser?.email,
      businessInfo?.userID,
      businessInfo?.userId
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter((value) => value.length > 0);

    return identifiers.some((value) => this.hiddenSetupGuideIdentifiers.has(value));
  }

  private readCompletedBannerDismissedState(): boolean {
    try {
      return localStorage.getItem(this.getCompletedBannerDismissedStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  private saveCompletedBannerDismissedState(value: boolean): void {
    try {
      if (value) {
        localStorage.setItem(this.getCompletedBannerDismissedStorageKey(), '1');
        return;
      }
      localStorage.removeItem(this.getCompletedBannerDismissedStorageKey());
    } catch {
      // Ignore localStorage errors.
    }
  }

  private syncBannerBodyClass(): void {
    if (this.shouldShowStatusStrip) {
      document.body.classList.add(this.pageBannerClass);
      return;
    }

    this.removeBannerBodyClass();
  }

  private removeBannerBodyClass(): void {
    document.body.classList.remove(this.pageBannerClass);
  }

  private extractStoreContentStatus(data: any): StoreContentStatus | null {
    const rawStatus =
      data?.storeContentStatus
      ?? data?.StoreContentStatus
      ?? data?.storecontentStatus
      ?? data?.storeContentstatus
      ?? data?.storecontentstatus
      ?? data?.status
      ?? data?.Status;
    if (rawStatus === null || rawStatus === undefined || rawStatus === '') {
      return null;
    }

    const normalizedStatus = String(rawStatus).trim().toLowerCase();
    if (normalizedStatus === 'inprogress' || normalizedStatus === 'in progress' || normalizedStatus === '0') {
      return StoreContentStatus.InProgress;
    }
    if (normalizedStatus === 'pendingbyclient' || normalizedStatus === 'pending by client' || normalizedStatus === '1') {
      return StoreContentStatus.PendingByClient;
    }
    if (normalizedStatus === 'completed' || normalizedStatus === '2') {
      return StoreContentStatus.Completed;
    }
    if (normalizedStatus === 'other' || normalizedStatus === '3') {
      return StoreContentStatus.Other;
    }

    return null;
  }

  private extractStoreContentFileUrl(data: any): string {
    return String(
      data?.storeContentFileUrl
      ?? data?.StoreContentFileUrl
      ?? data?.storecontentFileUrl
      ?? data?.storeContentfileUrl
      ?? data?.storecontentfileurl
      ?? data?.fileUrl
      ?? data?.FileUrl
      ?? ''
    ).trim();
  }

  private extractStoreContentShowNotificationBar(data: any): boolean | null {
    const rawValue =
      data?.storeContentShowNotificationBar
      ?? data?.StoreContentShowNotificationBar
      ?? data?.storecontentshownotificationbar;

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    if (typeof rawValue === 'boolean') {
      return rawValue;
    }

    const normalized = String(rawValue).trim().toLowerCase();
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') {
      return false;
    }

    if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') {
      return true;
    }

    return null;
  }

  private validateStoreContentFile(file: File): string | null {
    const fileName = String(file?.name || '').trim();
    const extension = fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '';
    if (!this.allowedStoreContentExtensions.includes(extension)) {
      return 'Only .doc, .docx, and .pdf files are allowed.';
    }

    if (Number(file?.size || 0) > this.maxStoreContentFileSize) {
      return 'File size exceeds 25 MB limit.';
    }

    return null;
  }

  private buildStoreContentUploadPayload(fileUrl: string, fileName: string): any {
    const title = fileName.replace(/\.[^/.]+$/, '') || 'Store Content File';
    return {
      FileUrl: fileUrl,
      FileName: fileName,
      Title: title,
      Status: 0,
      Notes: 'Uploaded from dashboard'
    };
  }

  private async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    if (typeof file.arrayBuffer === 'function') {
      return await file.arrayBuffer();
    }

    return await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (result instanceof ArrayBuffer) {
          resolve(result);
          return;
        }

        reject(new Error('Unable to read file.'));
      };
      reader.onerror = () => reject(reader.error || new Error('Unable to read file.'));
      reader.readAsArrayBuffer(file);
    });
  }

  private async yieldToBrowser(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  private async arrayBufferToBase64(buffer: ArrayBuffer, onProgress?: (progress: number) => void): Promise<string> {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 49152;
    let base64 = '';

    for (let start = 0; start < bytes.length; start += chunkSize) {
      const end = Math.min(start + chunkSize, bytes.length);
      let binary = '';
      for (let index = start; index < end; index++) {
        binary += String.fromCharCode(bytes[index]);
      }
      base64 += btoa(binary);

      if (onProgress) {
        onProgress(Math.min(99, Math.max(1, Math.round((end / bytes.length) * 100))));
      }

      await this.yieldToBrowser();
    }

    return base64;
  }

  private async fileToDataUrl(file: File, onProgress?: (progress: number) => void): Promise<string> {
    const buffer = await this.readFileAsArrayBuffer(file);
    const base64 = await this.arrayBufferToBase64(buffer, onProgress);
    const mimeType = String(file?.type || 'application/octet-stream').trim() || 'application/octet-stream';
    const dataUrl = `data:${mimeType};base64,${base64}`;

    if (!dataUrl) {
      throw new Error('Unable to encode file.');
    }

    return dataUrl;
  }

  private mapLowStockRow(item: any): ProductRow {
    const quantity = Number(item?.quantity ?? 0);
    const badgeType: ProductRow['badgeType'] =
      quantity <= 0 ? 'out' : quantity <= this.lowStockThreshold ? 'low' : 'in';
    const badgeText = badgeType === 'out' ? 'Out-of-stock' : badgeType === 'low' ? 'Low-stock' : 'In-stock';
    const formattedQty = this.formatInteger(quantity);

    return {
      name: this.getDashboardProductName(item),
      qty: formattedQty,
      image: this.resolveDashboardProductImage(item),
      badgeType,
      badge: `${badgeText} (${formattedQty})`
    };
  }

  private getDashboardProductName(item: any): string {
    return String(item?.productName || item?.itemName || '').trim();
  }

  private hasDashboardProductName(item: any): boolean {
    return this.getDashboardProductName(item).length > 0;
  }

  private isDeletedProduct(item: any): boolean {
    const productName = this.getDashboardProductName(item).toLowerCase();
    return item?.isDeleted === true || productName.endsWith('_deleted');
  }

  private isDefaultProductImageUrl(value: any): boolean {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (!normalized) {
      return true;
    }

    return normalized.includes('defaultproductimage') || normalized.includes('default_product.png');
  }

  private getDashboardSkuList(item: any): any[] {
    const skuCandidates = [
      item?.crsItemSKUs,
      item?.itemSKUs,
      item?.ItemSKUs,
      item?.skUs,
      item?.SKUs,
      item?.SkUs
    ];

    for (const candidate of skuCandidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private getFirstRealDashboardSkuImage(item: any): string {
    const skus = this.getDashboardSkuList(item);
    for (const sku of skus) {
      const skuImage = String(
        sku?.skuImage
        ?? sku?.SKUImage
        ?? sku?.image
        ?? sku?.Image
        ?? sku?.imageThumbnail
        ?? sku?.ImageThumbnail
        ?? ''
      ).trim();

      if (skuImage && !this.isDefaultProductImageUrl(skuImage)) {
        return skuImage;
      }
    }

    return '';
  }

  private normalizeDashboardImageUrl(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw || raw.includes('undefined') || raw.includes('null')) {
      return '';
    }

    let normalized = raw.replace(/\/Products\/Thumbnail\//i, '/Products/');
    try {
      // Decode first to avoid double-encoding URLs that already contain percent-encoded
      // characters (e.g. %28 → ( ), then re-encode to normalise any unencoded spaces etc.
      normalized = encodeURI(decodeURI(normalized));
    } catch {
      return '';
    }

    if (/^https?:\/\//i.test(normalized) || normalized.startsWith('assets/')) {
      return normalized;
    }

    return '';
  }

  private resolveDashboardProductImage(item: any): string {
    const pictures = Array.isArray(item?.itemPictures)
      ? item.itemPictures
      : Array.isArray(item?.ItemPictures)
        ? item.ItemPictures
        : [];

    const featuredPic = String(
      pictures.find((picture: any) => picture?.isFeature || picture?.isDefault)?.picture
      ?? pictures.find((picture: any) => picture?.IsFeature || picture?.IsDefault)?.picture
      ?? ''
    ).trim();
    const firstPic = String(pictures?.[0]?.picture ?? '').trim();
    const skuImage = this.getFirstRealDashboardSkuImage(item);

    const candidates = [
      item?.image,
      item?.Image,
      item?.imageThumbnail,
      item?.ImageThumbnail,
      item?.thumbnail,
      item?.Thumbnail,
      item?.productOfMonthPicture,
      item?.productOfMonthThumbnail,
      featuredPic,
      firstPic,
      skuImage
    ]
      .map((candidate) => String(candidate ?? '').trim())
      .filter((candidate) => !!candidate);

    const primaryCandidate =
      candidates.find((candidate) => !this.isDefaultProductImageUrl(candidate))
      ?? candidates[0]
      ?? '';

    const normalized = this.normalizeDashboardImageUrl(primaryCandidate);
    return normalized || this.defaultProductImage;
  }

  onProductImageError(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (!image) {
      return;
    }

    if (image.dataset['fallbackApplied'] === '1') {
      return;
    }

    image.dataset['fallbackApplied'] = '1';
    image.src = this.defaultProductImage;
  }

  private getEmptySummaryCards(): SummaryCard[] {
    return [
      {
        title: 'Net Sales',
        value: this.formatCurrency(0),
        trend: this.formatPercent(0),
        positiveTrend: true,
        hasTrend: false,
        inStore: this.formatCurrency(0),
        online: this.formatCurrency(0),
        highlighted: true
      },
      {
        title: 'Total Orders',
        value: this.formatInteger(0),
        trend: this.formatPercent(0),
        positiveTrend: true,
        hasTrend: false,
        inStore: this.formatInteger(0),
        online: this.formatInteger(0)
      },
      {
        title: 'Gross Profit',
        value: this.formatCurrency(0),
        trend: this.formatPercent(0),
        positiveTrend: true,
        hasTrend: false,
        inStore: this.formatCurrency(0),
        online: this.formatCurrency(0)
      },
      {
        title: 'Total Sales',
        value: this.formatCurrency(0),
        trend: this.formatPercent(0),
        positiveTrend: true,
        hasTrend: false,
        inStore: this.formatCurrency(0),
        online: this.formatCurrency(0)
      }
    ];
  }

  private formatCurrency(value: any): string {
    return GlobalService.formatNumberWithCurrencySign(value);
  }

  private formatInteger(value: any): string {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numericValue);
  }

  private formatPercent(value: number): string {
    const normalizedValue = Math.abs(Number(value || 0));
    const formattedValue = normalizedValue.toFixed(1).replace(/\.0$/, '');
    return `${formattedValue}%`;
  }
}
